import { useContext, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import AppContext from '../../contexts/AppContext';
import ConfigContext from '../../contexts/ConfigContext';
import DataContext from '../../contexts/DataContext';

import Button from '../../components/Button';
import CustomSelect from '../../components/CustomSelect';
import { appendScript } from '../../helpers';
import electricUtilities from '../../data/providers';

import Slide from '../components/Slide';
import { utilityProvider as utilityProviderPageData } from '../data/pages';
import type { Option } from '../types';

interface Tariff {
  tariffCode: string;
  tariffId: number;
}

interface Plan {
  lseId: number;
  lseName: string;
  tariffList: Tariff[];
}

export default function UtilityProvider() {
  const {
    currentPageIndex,
    setCurrentPageIndex,
    enphaseAccTariffs,
    changedState,
    setChangedState,
  } = useContext(AppContext);
  const { features } = useContext(ConfigContext);
  const { data, updateData } = useContext(DataContext);
  const dataRef = useRef(data);

  const [selectedOption, setSelectedOption] = useState<Option | null>(null);
  const [options, setOptions] = useState<Option[]>([]);

  const [searchParams, setSearchParams] = useSearchParams();

  const { dataKey, heading, nextPage, placeholder } = utilityProviderPageData;
  const { selectedZipCode, utilityProvider } = data;

  const utilityBlacklist = [
    'Ameren',
    'Bandera Electric Coop, Inc',
    'CenterPoint Energy',
    'Magic Valley Electric Coop Inc',
  ];

  const goToNextPage = () => {
    // set current page index
    setCurrentPageIndex(currentPageIndex + 1);

    // update page param and navigate to next page
    searchParams.set('page', nextPage);
    setSearchParams(searchParams);
  };

  const handleChange = (selectedOption: Option) => {
    setSelectedOption(selectedOption);
    updateData({
      ...data,
      [dataKey]: selectedOption.value,
    });

    dataLayer.push({
      event: 'seform_electric_utility_provider',
      event_action: 'Who is your electric utility provider?',
      event_label: selectedOption!.value,
    });

    goToNextPage();
  };

  const setEnphaseOptions = () => {
    if (Object.keys(enphaseAccTariffs).length === 0) return;

    const options: Option[] = [];
    enphaseAccTariffs.plans.forEach((plan: Plan) => {
      options.push({ value: plan.lseName, label: plan.lseName });
    });
    setOptions(options);

    if (!utilityProvider) {
      const selectedOption: Option = {
        label: enphaseAccTariffs.prefLseName,
        value: enphaseAccTariffs.prefLseName,
      };
      setSelectedOption(selectedOption);
      updateData({
        ...data,
        [dataKey]: selectedOption.value,
      });
    }
  };

  const setEnphaseRateStructureOptions = () => {
    if (Object.keys(enphaseAccTariffs).length === 0 || !selectedOption) return;

    const options: any = [];
    const plan = enphaseAccTariffs.plans.find(
      (plan: any) => plan.lseName === (selectedOption as any).value
    );

    if (!plan) return;
    plan.tariffList.forEach((option: any) => {
      options.push({ value: option.tariffCode, label: option.tariffCode });
    });

    const selectedRateOption: any = {
      label: plan.tariffList[0].tariffCode,
      value: plan.tariffList[0].tariffCode,
    };

    updateData({
      ...data,
      enphaseRateSelectedOption: selectedRateOption.value,
    });
  };

  useEffect(() => {
    const options: any = [];

    if (features.showPanelPlacement) {
      setEnphaseOptions();
    } else {
      if (utilityBlacklist.indexOf(data.utility) !== -1) return;
      const state = data.selectedState?.toUpperCase();

      if (typeof (electricUtilities as any)[state] === 'undefined') return;

      let utilityArray = (electricUtilities as any)[state];
      const dashIndex = utilityArray.indexOf('----------');
      if (dashIndex > -1) {
        const staticArray = utilityArray.slice(0, dashIndex);
        const toSortArray = utilityArray
          .slice(dashIndex, utilityArray.length - 1)
          .sort();
        toSortArray.push('Not Listed');
        utilityArray = staticArray.concat(toSortArray);
      }

      utilityArray.forEach((utility: any) => {
        if (utility === '----------') {
          options.push({ value: utility, label: utility, isDisabled: true });
        } else {
          options.push({ value: utility, label: utility });
        }
      });
    }

    setOptions(options);

    if (!changedState && !features.useUtilityHeadline) {
      if (utilityProvider) {
        const selectedOption: Option = {
          label: utilityProvider,
          value: utilityProvider,
        };
        setSelectedOption(selectedOption);

        const selectedRateOption: Option = {
          label: utilityProvider,
          value: utilityProvider,
        };
        updateData({
          ...data,
          [dataKey]: selectedOption.value,
          enphaseRateSelectedOption: selectedRateOption.value,
        });
      }
    } else if (!features.useUtilityHeadline) {
      updateData({
        ...data,
        utilityProvider: null,
      });
      setChangedState(false);
    }

    if (features.useUtilityHeadline) {
      if (utilityProvider) {
        const selectedOption: any = {
          value: utilityProvider,
          label: utilityProvider,
        };
        setSelectedOption(selectedOption);
      }
    } else {
      if (selectedZipCode) {
        window.updateCity = (response: any) => {
          let selectedCity = response.data.placename || response.city;
          if (selectedCity) {
            updateData({ ...dataRef.current, selectedCity });
          }
        };
        appendScript(
          `https://geoip.solarleadfactory.com/zip2state/?callback=updateCity&zip=${selectedZipCode}`,
          true
        );
      }
    }
  }, []);

  useEffect(() => {
    setEnphaseOptions();
  }, [enphaseAccTariffs]);

  useEffect(() => {
    setEnphaseRateStructureOptions();
  }, [selectedOption]);

  useEffect(() => {
    dataRef.current = data;
  }, [data]);

  return (
    <>
      <Slide heading={heading} />
      <p className="mt-[30px] md:mt-10 text-[16px] short:!text-[16px] md:text-[18px]">
        Electric Utility
      </p>
      <div className="w-full short:!w-[350px] max-w-[279px] md:max-w-[400px] md:w-[400px] h-10 md:h-[57px] mt-3 md:mt-4 mb-6 md:mb-10 text-[14px]">
        {!features.showPanelPlacement ||
        (options.length > 0 && selectedOption) ? (
          <CustomSelect
            onChange={handleChange}
            options={options}
            placeholder={placeholder}
            value={selectedOption}
            zukoId="utility-provider-select"
          />
        ) : (
          <div className="w-full h-full flex items-center">
            <span className="loader-dots"></span>
          </div>
        )}
      </div>
      <div className="mt-[50px] md:mt-[60px]">
        <Button
          action={() => {
            updateData({
              ...data,
              [dataKey]: utilityProvider,
            });

            dataLayer.push({
              event: 'seform_electric_utility_provider',
              event_action: 'Who is your electric utility provider?',
              event_label: selectedOption!.value,
            });

            goToNextPage();
          }}
          disabled={!utilityProvider}
          data-zuko-id="provider_continue"
        />
      </div>
    </>
  );
}
